if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.bundlePropertyName = this.dataset.bundlePropertyName || 'Bundle';
        this.emptyFilterMessage = this.dataset.emptyFilterMessage || 'No pouches available for this strength right now.';
        this.emptyListMessage = this.dataset.emptyListMessage || 'No pouches are available in this bundle collection yet.';
        this.toastTimeout = null;
        this.tierEffectTimeout = null;
        this.previousDiscountTier = null;
        this.undoQuantities = null;
        this.undoLabel = 'Undo';
        this.lastPreset = null;

        // getAttribute (not dataset) because dataset doesn't strip hyphens
        // that come before digits — `data-pack-5-discount` would map to
        // `dataset['pack-5Discount']`, NOT `dataset.pack5Discount`.
        this.discounts = {
          5: Number(this.getAttribute('data-pack-5-discount') || 0),
          7: Number(this.getAttribute('data-pack-7-discount') || 0),
          10: Number(this.getAttribute('data-pack-10-discount') || 0),
        };

        this.state = {
          packSize: Number(this.dataset.defaultPackSize || 5),
          activeStrengths: new Set(),
          subscribe: false,
          isSubmitting: false,
          variants: new Map(),
        };
      }

      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.cacheElements();
        this.readRows();
        this.renderStrengthFilters();
        this.bindEvents();
        this.render();
      }

      cacheElements() {
        this.packCards = Array.from(this.querySelectorAll('[data-pack-card]'));
        this.tierProgress = this.querySelector('[data-tier-progress]');
        this.strengthFilters = this.querySelector('[data-strength-filters]');
        this.filterButtons = Array.from(this.querySelectorAll('[data-strength-filter]'));
        this.presetButtons = Array.from(this.querySelectorAll('[data-preset]'));
        this.rows = Array.from(this.querySelectorAll('[data-byo-row]'));
        this.panel = this.querySelector('.bundle-builder__panel');
        this.selectedWrap = this.querySelector('[data-selected-wrap]');
        this.selectionList = this.querySelector('[data-selection-list]');
        this.selectionTotal = this.querySelector('[data-selection-total]');
        this.selectionEmpty = this.querySelector('[data-selection-empty]');
        this.undoButton = this.querySelector('[data-undo-selection]');
        this.clearButtons = Array.from(this.querySelectorAll('[data-clear-selection]'));
        this.presetHelp = this.querySelector('[data-preset-help]');
        this.selectedCountEls = Array.from(this.querySelectorAll('[data-selected-count], [data-sticky-selected-count]'));
        this.packCountEls = Array.from(this.querySelectorAll('[data-pack-count], [data-sticky-pack-count]'));
        this.subtotalEl = this.querySelector('[data-subtotal]');
        this.submitButton = this.querySelector('[data-submit-button]');
        this.submitText = this.querySelector('[data-submit-text]');
        this.stickySummary = this.querySelector('[data-sticky-summary]');
        this.tierUpsell = this.querySelector('[data-tier-upsell]');
        this.helperText = this.querySelector('[data-helper-text]');
        this.emptyState = this.querySelector('[data-empty-state]');
        this.toast = this.querySelector('[data-toast]');
        this.subscriptionControl = this.querySelector('[data-subscription-control]');
        this.subscriptionToggle = this.querySelector('[data-subscription-toggle]');
        this.subscriptionNote = this.querySelector('[data-subscription-note]');
        this.defaultSubscriptionNote = this.subscriptionNote ? this.subscriptionNote.textContent : '';
      }

      readRows() {
        this.rows.forEach((row) => {
          const id = Number(row.dataset.variantId);
          if (!id) return;

          this.state.variants.set(id, {
            id,
            productId: Number(row.dataset.productId || 0),
            title: row.dataset.title || '',
            variantTitle: row.dataset.variantTitle || '',
            flavor: row.dataset.flavor || row.dataset.title || '',
            strength: row.dataset.strength || 'Pouch',
            price: Number(row.dataset.price || 0),
            available: row.dataset.available === 'true',
            inventory: Number(row.dataset.inventory || 9999),
            sku: row.dataset.sku || '',
            sellingPlan: row.dataset.sellingPlan || '',
            sellingPlanName: row.dataset.sellingPlanName || '',
            quantity: 0,
            row,
          });
        });
      }

      bindEvents() {
        if (this.strengthFilters) {
          this.strengthFilters.addEventListener('click', (event) => {
            const button = event.target.closest('[data-strength-filter]');
            if (!button) return;
            this.setStrengthFilter(button.dataset.strengthFilter || 'all');
          });
        }

        this.presetButtons.forEach((button) => {
          button.addEventListener('click', () => this.applyPreset(button.dataset.preset || 'best'));
        });

        this.addEventListener('click', (event) => {
          const plus = event.target.closest('[data-qty-plus]');
          const minus = event.target.closest('[data-qty-minus]');
          const clear = event.target.closest('[data-clear-selection]');
          const remove = event.target.closest('[data-remove-selection]');
          const undo = event.target.closest('[data-undo-selection]');

          if (plus) this.changeQuantity(Number(plus.dataset.variantId), 1);
          if (minus) this.changeQuantity(Number(minus.dataset.variantId), -1);
          if (clear) this.clearSelection();
          if (remove) this.removeSelection(Number(remove.dataset.variantId));
          if (undo) this.undoSelection();
        });

        if (this.submitButton) this.submitButton.addEventListener('click', () => this.submit());
        if (this.subscriptionToggle) {
          this.subscriptionToggle.addEventListener('change', () => {
            this.state.subscribe = this.subscriptionToggle.checked;
            this.render();
          });
        }
      }

      setPackSize(size) {
        this.state.packSize = size || 5;
        this.render();
      }

      setStrengthFilter(strength) {
        if (!strength || strength === 'all') {
          this.state.activeStrengths.clear();
        } else if (this.state.activeStrengths.has(strength)) {
          this.state.activeStrengths.delete(strength);
        } else {
          this.state.activeStrengths.add(strength);
        }
        this.lastPreset = null;
        this.render();
      }

      changeQuantity(variantId, delta) {
        const item = this.state.variants.get(variantId);
        if (!item || !item.available || this.state.isSubmitting) return;

        if (delta > 0) {
          if (item.quantity >= item.inventory) {
            this.showToast(`${item.flavor} ${item.strength} has limited inventory.`);
            return;
          }
        }

        this.saveUndoState('Undo quantity change');
        item.quantity = Math.max(0, item.quantity + delta);
        this.lastPreset = null;
        this.render();
      }

      removeSelection(variantId) {
        const item = this.state.variants.get(variantId);
        if (!item || item.quantity <= 0 || this.state.isSubmitting) return;
        this.saveUndoState(`Restore ${item.flavor}`);
        item.quantity = 0;
        this.lastPreset = null;
        this.render();
      }

      applyPreset(type) {
        if (this.state.isSubmitting) return;
        this.saveUndoState('Undo quick start');

        const isRepeatedPreset = type === this.lastPreset && this.selectedCount > 0;
        const currentCount = this.selectedCount;
        let targetCount = this.discountTiers[0] || this.state.packSize;
        let shouldReplace = !isRepeatedPreset;

        if (isRepeatedPreset) {
          const nextTier = this.discountTiers.find((tier) => tier > currentCount);
          if (nextTier) {
            targetCount = nextTier;
            shouldReplace = false;
          } else {
            targetCount = this.maxDiscountTier || this.state.packSize;
            shouldReplace = true;
          }
        }

        if (shouldReplace) this.clearSelection(false, false);
        const fillStartCount = this.selectedCount;

        let candidates = this.availableVariants.filter(
          (item) => this.state.activeStrengths.size === 0 || this.state.activeStrengths.has(item.strength)
        );
        if (type === 'starter') {
          candidates = candidates.filter((item) => ['56mg', '72mg'].includes(item.strength));
        } else if (type === 'focus') {
          candidates = candidates.filter((item) => ['72mg', '125mg'].includes(item.strength));
        } else if (type === 'max') {
          candidates = candidates.filter((item) => ['125mg', '225mg'].includes(item.strength));
        }

        if (!candidates.length) {
          this.undoSelection(false);
          this.showToast('No available cans match those strengths. Choose another strength or select All.');
          this.render();
          return;
        }

        const bestSellerOrder = ['Wintergreen', 'Watermelon Ice', 'Blue Raspberry', 'Spearmint', 'Pink Lemonade', 'Mango Crush', 'Red Gummy Bear'];
        const ordered = [...candidates].sort((a, b) => {
          if (type !== 'best') return a.flavor.localeCompare(b.flavor);
          const aRank = bestSellerOrder.findIndex((flavor) => a.flavor.toLowerCase().includes(flavor.toLowerCase()));
          const bRank = bestSellerOrder.findIndex((flavor) => b.flavor.toLowerCase().includes(flavor.toLowerCase()));
          return (aRank < 0 ? 999 : aRank) - (bRank < 0 ? 999 : bRank) || a.flavor.localeCompare(b.flavor);
        });
        let cursor = 0;
        let lastCount = -1;

        while (this.selectedCount < targetCount) {
          if (this.selectedCount === lastCount) break;
          lastCount = this.selectedCount;

          let madeProgress = false;
          for (let index = 0; index < ordered.length && this.selectedCount < targetCount; index += 1) {
            const item = ordered[cursor % ordered.length];
            cursor += 1;
            if (item.quantity >= item.inventory) continue;
            item.quantity += 1;
            madeProgress = true;
          }

          if (!madeProgress) break;
        }

        if (this.selectedCount < targetCount) {
          this.showToast(`Only ${this.selectedCount} cans are available for that mix.`);
        }

        this.lastPreset = type;
        this.render();
        if (this.selectedCount > fillStartCount) {
          this.publishQuickStartUsage(type, targetCount, isRepeatedPreset);
        }
      }

      publishQuickStartUsage(preset, targetCount, repeated) {
        const analytics = window.Shopify && window.Shopify.analytics;
        if (!analytics || typeof analytics.publish !== 'function') return;

        try {
          const published = analytics.publish('lyvwel:bundle_quick_start_used', {
            preset,
            selected_count: this.selectedCount,
            target_count: targetCount,
            repeated,
            active_strengths: Array.from(this.state.activeStrengths).sort(),
          });
          if (published && typeof published.catch === 'function') published.catch(() => {});
        } catch (error) {
          // Analytics must not interfere with bundle selection.
        }
      }

      clearSelection(shouldRender = true, allowUndo = true) {
        if (allowUndo && this.selectedCount > 0) this.saveUndoState('Undo clear mix');
        this.state.variants.forEach((item) => {
          item.quantity = 0;
        });
        this.lastPreset = null;
        if (shouldRender) this.render();
      }

      saveUndoState(label = 'Undo') {
        this.undoQuantities = new Map();
        this.state.variants.forEach((item, id) => {
          if (item.quantity > 0) this.undoQuantities.set(id, item.quantity);
        });
        this.undoLabel = label;
      }

      undoSelection(shouldRender = true) {
        if (!this.undoQuantities) return;
        const quantities = this.undoQuantities;
        this.state.variants.forEach((item, id) => {
          item.quantity = quantities.get(id) || 0;
        });
        this.undoQuantities = null;
        this.undoLabel = 'Undo';
        this.lastPreset = null;
        if (shouldRender) this.render();
      }

      render() {
        this.renderPackCards();
        this.renderFilters();
        this.renderRows();
        this.renderSelectedList();
        this.renderSummary();
        this.renderSubscriptionControl();
        this.renderTierEffect();
      }

      renderSubscriptionControl() {
        if (!this.subscriptionControl) return;

        const hasAnySellingPlan = Array.from(this.state.variants.values()).some((item) => item.sellingPlan);
        this.subscriptionControl.hidden = !hasAnySellingPlan;

        if (!hasAnySellingPlan) {
          this.state.subscribe = false;
          if (this.subscriptionToggle) this.subscriptionToggle.checked = false;
          return;
        }

        if (this.subscriptionToggle && this.subscriptionToggle.checked !== this.state.subscribe) {
          this.subscriptionToggle.checked = this.state.subscribe;
        }

        if (this.subscriptionNote) {
          const selectedWithoutPlan = this.state.subscribe && this.selectedVariants.some((item) => !item.sellingPlan);
          if (selectedWithoutPlan) {
            this.subscriptionNote.textContent = 'Some selected pouches are not configured for subscriptions yet.';
          } else if (this.state.subscribe && this.activeDiscountPct) {
            this.subscriptionNote.textContent = `Subscription enabled with your ${this.activeDiscountPct}% bundle discount tier.`;
          } else {
            this.subscriptionNote.textContent = this.defaultSubscriptionNote;
          }
        }
      }

      renderStrengthFilters() {
        if (!this.strengthFilters) return;

        const strengths = Array.from(
          new Set(
            Array.from(this.state.variants.values())
              .map((item) => item.strength)
              .filter(Boolean)
          )
        ).sort((a, b) => this.strengthSortValue(a) - this.strengthSortValue(b) || a.localeCompare(b, undefined, { numeric: true }));

        const filterGroup = this.strengthFilters.closest('.bundle-builder__control-group');
        if (filterGroup) filterGroup.hidden = strengths.length === 0;

        this.strengthFilters.innerHTML = '';
        if (strengths.length === 0) return;

        this.strengthFilters.appendChild(this.createStrengthFilterButton('all', 'All'));

        strengths.forEach((strength) => {
          this.strengthFilters.appendChild(this.createStrengthFilterButton(strength, strength));
        });

        this.filterButtons = Array.from(this.querySelectorAll('[data-strength-filter]'));
      }

      createStrengthFilterButton(value, label) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'bundle-builder__filter';
        button.dataset.strengthFilter = value;
        button.setAttribute('aria-pressed', 'false');
        button.textContent = label;
        return button;
      }

      renderPackCards() {
        const activeTier = this.selectedDiscountTier;
        const maxTier = this.maxDiscountTier || activeTier;
        const progress = maxTier > 0 ? Math.min((this.selectedCount / maxTier) * 100, 100) : 0;

        if (this.tierProgress) {
          this.tierProgress.style.setProperty('--bundle-tier-progress', `${progress}%`);
        }

        this.packCards.forEach((card) => {
          const size = Number(card.dataset.packSize || 0);
          const isActive = size === activeTier;
          const isEarned = size > 0 && this.selectedCount >= size;
          const isNext = size > this.selectedCount && size === this.nextDiscountTier;

          card.classList.toggle('is-active', isActive);
          card.classList.toggle('is-earned', isEarned);
          card.classList.toggle('is-next', isNext);
          card.setAttribute('aria-current', isActive ? 'step' : 'false');
        });
      }

      renderFilters() {
        this.filterButtons.forEach((button) => {
          const value = button.dataset.strengthFilter;
          const isActive = value === 'all' ? this.state.activeStrengths.size === 0 : this.state.activeStrengths.has(value);
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      renderRows() {
        let visibleRows = 0;
        const discountPct = this.activeDiscountPct;
        const isDiscountActive = discountPct > 0;

        this.state.variants.forEach((item) => {
          const isFiltered = this.state.activeStrengths.size > 0 && !this.state.activeStrengths.has(item.strength);
          const isVisible = !isFiltered;
          const isSelected = item.quantity > 0;
          const plus = item.row.querySelector('[data-qty-plus]');
          const minus = item.row.querySelector('[data-qty-minus]');
          const value = item.row.querySelector('[data-qty-value]');
          const rowPrice = item.row.querySelector('[data-row-price]');

          item.row.hidden = !isVisible;
          item.row.classList.toggle('is-selected', isSelected);
          item.row.classList.toggle('is-unavailable', !item.available);
          item.row.setAttribute('aria-hidden', isVisible ? 'false' : 'true');

          if (isVisible) visibleRows += 1;
          if (value) value.textContent = String(item.quantity);
          if (rowPrice) this.renderRowPrice(rowPrice, item.price, isSelected, isDiscountActive, discountPct);
          if (minus) minus.disabled = item.quantity <= 0 || this.state.isSubmitting;
          if (plus) {
            plus.disabled =
              !item.available ||
              this.state.isSubmitting ||
              item.quantity >= item.inventory;
          }
        });

        if (this.emptyState) {
          this.emptyState.hidden = this.rows.length > 0 && visibleRows > 0;
          if (this.rows.length > 0 && visibleRows === 0) this.emptyState.textContent = this.emptyFilterMessage;
        }

        if (!this.helperText) return;
        if (this.rows.length === 0) {
          this.helperText.textContent = this.emptyListMessage;
          return;
        }

        const nextTier = this.nextDiscountTier;
        const currentTier = this.selectedDiscountTier;
        if (!currentTier && nextTier) {
          const nextSavings = this.discounts[nextTier] || 0;
          const nextRemaining = nextTier - this.selectedCount;
          this.helperText.textContent = `Add ${nextRemaining} more ${nextRemaining === 1 ? 'can' : 'cans'} to save ${nextSavings}%.`;
        } else if (nextTier && nextTier > this.selectedCount) {
          const nextSavings = this.discounts[nextTier] || 0;
          const nextRemaining = nextTier - this.selectedCount;
          this.helperText.innerHTML =
            `<strong>Save ${currentTier ? this.discounts[currentTier] : 0}% unlocked.</strong> Add ${nextRemaining} more for an additional discount: ${nextSavings}% off.`;
        } else if (currentTier) {
          this.helperText.innerHTML = `${this.selectedCount} selected. <strong>Max discount unlocked: ${this.discounts[currentTier]}% off.</strong>`;
        } else {
          this.helperText.textContent = `${this.selectedCount} selected. Ready to add.`;
        }
      }

      renderRowPrice(element, price, isSelected, isDiscountActive, discountPct) {
        if (isSelected && isDiscountActive) {
          const discountedPrice = this.discountedPrice(price, discountPct);
          element.innerHTML =
            `<span class="bundle-builder__row-price-final">${this.formatMoney(discountedPrice)}/can</span>` +
            `<span class="bundle-builder__row-price-strike">${this.formatMoney(price)}</span>` +
            `<span class="bundle-builder__row-price-save">Save ${discountPct}%</span>`;
        } else {
          element.textContent = `${this.formatMoney(price)}/can`;
        }
      }

      renderSelectedList() {
        const selected = this.selectedVariants;
        if (this.selectedWrap) this.selectedWrap.hidden = selected.length === 0 && !this.undoQuantities;
        if (this.selectionTotal) {
          this.selectionTotal.textContent = this.selectedCount === 1 ? '1 selected' : `${this.selectedCount} selected`;
        }
        if (this.selectionEmpty) this.selectionEmpty.hidden = selected.length > 0;
        if (this.undoButton) {
          this.undoButton.hidden = !this.undoQuantities;
          this.undoButton.textContent = this.undoLabel;
        }
        this.clearButtons.forEach((button) => {
          button.disabled = selected.length === 0;
        });
        if (!this.selectionList) return;

        this.selectionList.hidden = selected.length === 0;
        this.selectionList.innerHTML = '';
        selected.forEach((item) => {
          const listItem = document.createElement('li');
          listItem.className = 'bundle-builder__selected-item';
          listItem.innerHTML = `
            <div class="bundle-builder__selected-copy">
              <span>${this.escapeHtml(item.flavor)}</span>
              <small>${this.escapeHtml(item.strength)}</small>
            </div>
            <div class="bundle-builder__selected-controls" aria-label="Adjust ${this.escapeHtml(item.flavor)} ${this.escapeHtml(item.strength)} quantity">
              <button type="button" data-qty-minus data-variant-id="${item.id}" aria-label="Remove one ${this.escapeHtml(item.flavor)}">−</button>
              <strong>${item.quantity}</strong>
              <button type="button" data-qty-plus data-variant-id="${item.id}" aria-label="Add one ${this.escapeHtml(item.flavor)}">+</button>
              <button type="button" class="bundle-builder__selected-remove" data-remove-selection data-variant-id="${item.id}" aria-label="Remove ${this.escapeHtml(item.flavor)} from your mix">Remove</button>
            </div>
          `;
          this.selectionList.appendChild(listItem);
        });

        this.renderPresetState();
      }

      renderPresetState() {
        const nextTier = this.discountTiers.find((tier) => tier > this.selectedCount);
        this.presetButtons.forEach((button) => {
          const isCurrent = button.dataset.preset === this.lastPreset && this.selectedCount > 0;
          button.classList.toggle('is-active', isCurrent);
          button.setAttribute('aria-pressed', isCurrent ? 'true' : 'false');
          if (!button.dataset.baseLabel) button.dataset.baseLabel = button.textContent.trim();
          if (isCurrent && nextTier) {
            button.textContent = `${button.dataset.baseLabel} · Add to ${nextTier}`;
          } else if (isCurrent && this.selectedCount >= this.maxDiscountTier) {
            button.textContent = `${button.dataset.baseLabel} · Remix`;
          } else {
            button.textContent = button.dataset.baseLabel;
          }
        });

        if (!this.presetHelp) return;
        if (this.lastPreset && nextTier) {
          this.presetHelp.textContent = `Tap the active mix again to add ${nextTier - this.selectedCount} cans and reach ${nextTier}.`;
        } else if (this.lastPreset && this.selectedCount >= this.maxDiscountTier) {
          this.presetHelp.textContent = 'Tap the active mix again for a fresh 10-can assortment.';
        } else if (this.state.activeStrengths.size > 0) {
          this.presetHelp.textContent = 'Quick Start will only use your selected strengths.';
        } else {
          this.presetHelp.textContent = 'Tap again to grow your mix from 5 to 7 to 10 cans.';
        }
      }

      renderSummary() {
        const selectedCount = this.selectedCount;
        const displayPackCount = this.displayPackCount;
        const currentTier = this.selectedDiscountTier;
        const nextTier = this.nextDiscountTier;
        const discountPct = this.activeDiscountPct;
        const isQualified = discountPct > 0;
        const discountedSubtotal = isQualified ? this.discountedPrice(this.subtotal, discountPct) : this.subtotal;

        if (this.stickySummary) {
          this.stickySummary.classList.toggle('is-visible', selectedCount > 0);
        }

        this.selectedCountEls.forEach((element) => {
          element.textContent = String(selectedCount);
        });
        this.packCountEls.forEach((element) => {
          element.textContent = String(displayPackCount);
        });
        if (this.subtotalEl) {
          if (isQualified && this.subtotal > 0) {
            this.subtotalEl.innerHTML =
              `<span class="bundle-builder__price-final">${this.formatMoney(discountedSubtotal)}</span>` +
              `<span class="bundle-builder__price-strike">${this.formatMoney(this.subtotal)}</span>` +
              `<span class="bundle-builder__price-save">Save ${discountPct}%</span>`;
          } else {
            this.subtotalEl.textContent = this.formatMoney(this.subtotal);
          }
        }

        if (!this.submitButton || !this.submitText) return;
        this.submitButton.disabled = !isQualified || this.state.isSubmitting;
        this.submitButton.classList.toggle('loading', this.state.isSubmitting);
        this.renderTierUpsell(isQualified, nextTier, selectedCount);

        if (this.state.isSubmitting) {
          this.submitText.textContent = 'Adding...';
        } else if (isQualified) {
          this.submitText.textContent = 'Add to cart';
        } else if (nextTier) {
          const remaining = nextTier - selectedCount;
          const savings = this.discounts[nextTier] || 0;
          this.submitText.textContent =
            remaining === 1
              ? `Add 1 more · Save ${savings}%`
              : `Add ${remaining} more · Save ${savings}%`;
        } else if (discountPct > 0) {
          const remaining = displayPackCount - selectedCount;
          this.submitText.textContent =
            remaining === 1
              ? `Add 1 more · Save ${discountPct}%`
              : `Add ${remaining} more · Save ${discountPct}%`;
        } else {
          const remaining = Math.max(1, displayPackCount - selectedCount);
          this.submitText.textContent =
            remaining === 1 ? 'Add 1 more can' : `Add ${remaining} more cans`;
        }
      }

      renderTierUpsell(isQualified, nextTier, selectedCount) {
        if (!this.tierUpsell) return;

        if (!isQualified || !nextTier || selectedCount >= nextTier) {
          this.tierUpsell.hidden = true;
          this.tierUpsell.textContent = '';
          return;
        }

        const nextRemaining = nextTier - selectedCount;
        const nextSavings = this.discounts[nextTier] || 0;
        this.tierUpsell.hidden = false;
        this.tierUpsell.textContent =
          `Add ${nextRemaining} more ${nextRemaining === 1 ? 'can' : 'cans'} for an additional discount: ${nextSavings}% off`;
      }

      renderTierEffect() {
        const currentTier = this.selectedDiscountTier;
        if (this.previousDiscountTier === null) {
          this.previousDiscountTier = currentTier;
          return;
        }

        if (currentTier === this.previousDiscountTier) return;

        const effectType = currentTier > this.previousDiscountTier ? 'unlock' : 'lock';
        const discountPct = this.discounts[currentTier] || 0;
        const message =
          effectType === 'unlock'
            ? `Discount unlocked: ${discountPct}% off`
            : currentTier
              ? `Adjusted to ${discountPct}% off`
              : 'Discount locked until the next tier';

        this.playTierEffect(effectType, message);
        this.previousDiscountTier = currentTier;
      }

      playTierEffect(type, message) {
        const target = this.panel || this;
        let effect = this.querySelector('[data-tier-effect]');
        if (!effect) {
          effect = document.createElement('div');
          effect.className = 'bundle-builder__tier-effect';
          effect.dataset.tierEffect = '';
          effect.setAttribute('aria-live', 'polite');
          effect.setAttribute('aria-atomic', 'true');
          target.appendChild(effect);
        }

        clearTimeout(this.tierEffectTimeout);
        effect.className = `bundle-builder__tier-effect bundle-builder__tier-effect--${type}`;
        effect.textContent = message;

        this.classList.remove('is-tier-unlocking', 'is-tier-locking');
        this.classList.add(type === 'unlock' ? 'is-tier-unlocking' : 'is-tier-locking');

        this.selectedVariants.forEach((item) => {
          item.row.classList.remove('is-tier-pulse');
          void item.row.offsetWidth;
          item.row.classList.add('is-tier-pulse');
        });

        this.tierEffectTimeout = setTimeout(() => {
          effect.classList.add('is-hiding');
          this.classList.remove('is-tier-unlocking', 'is-tier-locking');
          this.rows.forEach((row) => row.classList.remove('is-tier-pulse'));
        }, 1800);
      }

      buildCartItems() {
        const bundleId = `BYO-${Date.now()}`;
        const bundleTier = this.selectedDiscountTier || this.state.packSize;
        const bundleName = `BYO ${bundleTier}-Pack Mix`;
        const discountPct = this.activeDiscountPct;

        return this.selectedVariants.map((item) => {
          const cartItem = {
            id: item.id,
            quantity: item.quantity,
            properties: {
              ['_' + this.bundlePropertyName.replace(/^_+/, '')]: bundleName,
              '_Bundle ID': bundleId,
              '_Bundle Tier': `${bundleTier}-Pack`,
              '_Bundle Discount': `${discountPct}%`,
              '_Bundle Discounted Unit Price': this.formatMoney(this.discountedPrice(item.price, discountPct)),
            },
          };

          if (this.state.subscribe && item.sellingPlan) {
            cartItem.selling_plan = Number(item.sellingPlan);
            cartItem.properties._Subscription = item.sellingPlanName || 'Subscription';
          }
          return cartItem;
        });
      }

      async submit() {
        if (this.state.isSubmitting || !this.activeDiscountPct) return;
        if (this.state.subscribe && this.selectedVariants.some((item) => !item.sellingPlan)) {
          this.showToast('One or more selected pouches are not configured for subscriptions yet.');
          return;
        }

        this.state.isSubmitting = true;
        this.renderSummary();

        try {
          const sections = this.sectionsToRender;
          const body = {
            items: this.buildCartItems(),
            sections,
            sections_url: window.location.pathname,
          };

          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(body),
          });

          const parsedState = await response.json();
          if (!response.ok) throw new Error(parsedState.description || parsedState.message || 'Could not add mix to cart.');

          if (parsedState.sections) {
            this.renderCartSections(parsedState.sections, parsedState);
          } else {
            await this.fetchAndRenderCartSections();
          }

          this.clearSelection(false, false);
          this.undoQuantities = null;
          // Cart drawer opening is the confirmation — skip toast to avoid
          // overlapping the drawer's checkout buttons on mobile.

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bundle-builder', cartData: parsedState });
          }
        } catch (error) {
          this.showToast(error.message || 'Something went wrong. Please try again.');
        } finally {
          this.state.isSubmitting = false;
          this.render();
        }
      }

      async fetchAndRenderCartSections() {
        const response = await fetch(`${window.location.pathname}?sections=${this.sectionsToRender.join(',')}`);
        const sections = await response.json();
        this.renderCartSections(sections);
      }

      renderCartSections(sections, parsedState = null) {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && parsedState && typeof drawer.renderContents === 'function') {
          drawer.renderContents(parsedState);
          // Keep the bundle path defensive if the drawer was rendered from an
          // empty cart immediately before opening.
          drawer.classList.remove('is-empty');
        } else {
          this.replaceSection(sections['cart-drawer'], '#CartDrawer');
          this.openCartDrawer();
        }

        this.replaceSection(sections['main-cart-items'], '#main-cart-items .js-contents');
        this.replaceSection(sections['main-cart-footer'], '#main-cart-footer .js-contents');
        this.replaceSection(sections['cart-icon-bubble'], '#cart-icon-bubble', '.shopify-section');
        this.replaceSection(sections['cart-live-region-text'], '#cart-live-region-text', '.shopify-section');
      }

      replaceSection(html, selector, fallbackSelector = null) {
        if (!html) return;
        const target = document.querySelector(selector);
        if (!target) return;

        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const source =
          documentFragment.querySelector(selector) ||
          (fallbackSelector ? documentFragment.querySelector(fallbackSelector) : null) ||
          documentFragment.querySelector('.shopify-section');
        if (source) target.innerHTML = source.innerHTML;
      }

      openCartDrawer() {
        const drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      }

      showToast(message) {
        if (!this.toast) return;
        this.toast.textContent = message;
        this.toast.hidden = false;
        clearTimeout(this.toastTimeout);
        this.toastTimeout = setTimeout(() => {
          this.toast.hidden = true;
        }, 3500);
      }

      formatMoney(cents) {
        const value = Math.round(Number(cents || 0));
        if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
          return window.Shopify.formatMoney(value, this.moneyFormat);
        }
        return `$${(value / 100).toFixed(2)}`;
      }

      discountedPrice(price, discountPct) {
        return Math.round(Number(price || 0) * (1 - Number(discountPct || 0) / 100));
      }

      escapeHtml(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }

      strengthSortValue(strength) {
        const match = String(strength).match(/\d+/);
        return match ? Number(match[0]) : 9999;
      }

      get selectedVariants() {
        return Array.from(this.state.variants.values()).filter((item) => item.quantity > 0);
      }

      get availableVariants() {
        return Array.from(this.state.variants.values()).filter((item) => item.available && item.inventory > 0);
      }

      get selectedCount() {
        return this.selectedVariants.reduce((sum, item) => sum + item.quantity, 0);
      }

      get subtotal() {
        return this.selectedVariants.reduce((sum, item) => sum + item.quantity * item.price, 0);
      }

      get discountTiers() {
        return Object.keys(this.discounts)
          .map(Number)
          .filter((tier) => tier > 0 && this.discounts[tier] > 0)
          .sort((a, b) => a - b);
      }

      get packSizes() {
        return Array.from(
          new Set([
            ...this.packCards.map((card) => Number(card.dataset.packSize || 0)),
            ...Object.keys(this.discounts).map(Number),
            this.state.packSize,
          ])
        )
          .filter((size) => size > 0)
          .sort((a, b) => a - b);
      }

      get maxPackSize() {
        return this.packSizes[this.packSizes.length - 1] || this.state.packSize;
      }

      get maxDiscountTier() {
        return this.discountTiers[this.discountTiers.length - 1] || 0;
      }

      get selectedDiscountTier() {
        return this.discountTiers.reduce((activeTier, tier) => (this.selectedCount >= tier ? tier : activeTier), 0);
      }

      get nextDiscountTier() {
        return this.discountTiers.find((tier) => tier > this.selectedCount) || 0;
      }

      get displayPackCount() {
        if (!this.selectedCount) return this.nextDiscountTier || this.state.packSize;
        if (!this.selectedDiscountTier) return this.nextDiscountTier || this.state.packSize;
        if (this.selectedCount < this.state.packSize) return this.state.packSize;
        return this.nextDiscountTier || this.maxDiscountTier || this.state.packSize;
      }

      get activeDiscountPct() {
        return this.discounts[this.selectedDiscountTier] || 0;
      }

      get sectionsToRender() {
        return ['cart-drawer', 'main-cart-items', 'main-cart-footer', 'cart-icon-bubble', 'cart-live-region-text'];
      }
    }
  );
}

(() => {
const scrollToPremadeBundles = (behavior = 'auto') => {
  const target = document.getElementById('pre-made-bundles') || document.querySelector('.lw-bcmp');
  if (!target) return;

  const headerOffset = 120;
  const top = target.getBoundingClientRect().top + window.scrollY - headerOffset;
  window.scrollTo({ top: Math.max(0, top), behavior });
};

const handlePremadeBundleHash = () => {
  if (window.location.hash === '#pre-made-bundles') {
    window.requestAnimationFrame(() => scrollToPremadeBundles());
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', handlePremadeBundleHash, { once: true });
} else {
  handlePremadeBundleHash();
}

window.addEventListener('hashchange', handlePremadeBundleHash);

document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href="#pre-made-bundles"]');
  if (!link) return;

  event.preventDefault();
  window.history.pushState(null, '', '#pre-made-bundles');
  scrollToPremadeBundles('smooth');
});
})();
